import React, { useState, useRef, useLayoutEffect, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const Wrapper = styled.div`
  position: relative;
  min-width: 0;
  width: 100%;
`;

const SeeMoreButton = styled.button`
  position: absolute;
  bottom: 0;
  inset-inline-end: 0;
  border: none;
  cursor: pointer;
  font: inherit;
  color: inherit;
  font-weight: 600;
  padding: 0 0 0 0.35rem;
  line-height: inherit;
  background: ${(p) => p.$fadeBackground};
  box-shadow: -10px 0 10px 0 ${(p) => p.$fadeBackground};
`;

const UnclampedDescription = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
`;

const ClampedDescription = styled(UnclampedDescription)`
  -webkit-line-clamp: 4;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
`;

function ClampedText({ children, maxWidth, fadeBackground = 'white' }) {
  const [showDescriptionMoreButton, setShowDescriptionMoreButton] = useState(false);
  const [showMoreDescription, setShowMoreDescription] = useState(false);
  const contentRef = useRef(null);
  const showSeeMore = showDescriptionMoreButton && !showMoreDescription;

  const measureOverflow = useCallback(() => {
    if (showMoreDescription) return;
    const el = contentRef.current;
    if (!el) return;
    setShowDescriptionMoreButton(el.scrollHeight > el.clientHeight + 1);
  }, [showMoreDescription]);

  useLayoutEffect(() => {
    measureOverflow();
  }, [measureOverflow, maxWidth, children]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => measureOverflow());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureOverflow]);

  return (
    <Wrapper style={{ maxWidth }}>
      {showMoreDescription ? (
        <UnclampedDescription ref={contentRef}>{children}</UnclampedDescription>
      ) : (
        <ClampedDescription ref={contentRef}>
          {children}
        </ClampedDescription>
      )}
      {showSeeMore && (
        <SeeMoreButton
          type="button"
          $fadeBackground={fadeBackground}
          onClick={() => setShowMoreDescription(true)}
        >
          See more
        </SeeMoreButton>
      )}
    </Wrapper>
  );
}

ClampedText.propTypes = {
  children: PropTypes.any,
  maxWidth: PropTypes.string,
  fadeBackground: PropTypes.string,
};
export default ClampedText;
